import { DraggableProvidedDragHandleProps } from "@hello-pangea/dnd";
import { Delete, SettingsOutlined } from "@mui/icons-material";
import { Box, Button, IconButton, Stack, Tooltip, Typography } from "@mui/material";
import { BackPageButtonSearchParamName } from "components/buttons/BackPageButton/types";
import TextFieldControl from "components/form/TextFieldControl";
import ROUTES from "constants/routes";
import { ConfirmModalContext } from "context/confirmModalContext";
import { CompetencyMapSelectedParts } from "helpers/competencyMapSelectParts";
import { gradeColor } from "helpers/index";
import VALIDATION from "helpers/validation";
import withSelectionCheckbox, { WithSelectionCheckboxInjectedProps } from "hocs/withSelectionCheckbox";
import qs from "qs";
import { useContext } from "react";
import { FieldArrayWithId } from "react-hook-form";
import { Link, useParams } from "react-router-dom";
import { ConstructorTabContext } from "../../context";
import {
  CompetencyMapInputs,
  isKnowledgeBlockCompetencyFieldsNonNullable,
  isSkillFieldsNonNullable,
  KnowledgeBlockCompetencyOption,
  SkillLiteralType,
  SkillsLiteralType,
} from "../types";

interface SkillProps {
  name: SkillLiteralType;
  isEditable: boolean;
  skill: FieldArrayWithId<CompetencyMapInputs, SkillsLiteralType, "skillId">;
  knowledgeBlock: KnowledgeBlockCompetencyOption;
  remove: () => void;
  dragHandleProps: DraggableProvidedDragHandleProps | null;
  isLastField?: boolean;
}

const Skill = ({
  name,
  isEditable,
  skill,
  knowledgeBlock,
  remove,
  dragHandleProps,
  isLastField = false,
  selectionMode,
  selectionCheckboxNode,
}: SkillProps & WithSelectionCheckboxInjectedProps) => {
  const isNewSkill = !skill.id;
  const { mapId } = useParams();
  const { handleOpen } = useContext(ConfirmModalContext);
  const { setSkillsIdsToDelete, onOpenGenerateQuestionsForCMModal } = useContext(ConstructorTabContext);

  const handleRemoveSkill = () => {
    handleOpen({
      title: "Вы уверены, что хотите удалить навык из блока знаний?",
      handleClick: () => {
        remove();

        const skillId = skill.id;
        if (skillId) {
          setSkillsIdsToDelete((prev) => [...prev, skillId]);
        }
      },
    });
  };

  const handleOpenGenerateModal = () => {
    if (isKnowledgeBlockCompetencyFieldsNonNullable(knowledgeBlock) && isSkillFieldsNonNullable(skill)) {
      const selectParts: CompetencyMapSelectedParts = {
        knowledgeBockIds: [knowledgeBlock.id],
        skillIds: [skill.id],
      };

      onOpenGenerateQuestionsForCMModal(selectParts);
    }
  };

  return (
    <div {...dragHandleProps}>
      <Box
        sx={{
          pb: 1,
          px: 1,
          display: "grid",
          gridTemplateColumns: selectionMode
            ? "auto minmax(180px, 2fr) minmax(80px, 1fr) minmax(200px, 4fr) minmax(120px, 1fr)"
            : "minmax(180px, 2fr) minmax(80px, 1fr) minmax(200px, 4fr) minmax(120px, 1fr)",
          gap: 2,
          alignItems: "center",
          "& > *": { minWidth: 0 },
          ...(!isLastField ? { borderBottomWidth: 1, borderBottomStyle: "solid", borderBottomColor: "divider" } : {}),
        }}>
        {selectionCheckboxNode}
        <Box>
          {isNewSkill ? (
            <TextFieldControl
              placeholder='Название навыка'
              name={`${name}.title`}
              disabled={!isEditable}
              rules={{ required: true, ...VALIDATION.competencyMapTitle }}
              color='secondary'
              sx={{ width: 200 }}
            />
          ) : (
            <Tooltip title='Навык'>
              <Stack
                direction='row'
                alignItems='center'
                spacing={1}>
                <SettingsOutlined fontSize='small' />
                <Button
                  size='small'
                  component={Link}
                  to={{
                    pathname: `/${ROUTES.SKILL}/view/${skill.id}`,
                    search: mapId
                      ? qs.stringify({ [BackPageButtonSearchParamName.COMPETENCY_MAP_ID]: mapId })
                      : undefined,
                  }}
                  sx={{ paddingLeft: 0, color: "blue", textDecoration: "underline", justifyContent: "flex-start" }}>
                  {skill.title}
                </Button>
              </Stack>
            </Tooltip>
          )}
        </Box>
        <Box>
          {isNewSkill ? null : (
            <Tooltip title='Грейд'>
              <Box>
                {skill.grades?.length ? (
                  skill.grades?.map((grade) => (
                    <Typography
                      key={`${skill.skillId}-${grade}`}
                      sx={{ color: `${gradeColor(grade)}.contrast` }}>
                      {grade}
                    </Typography>
                  ))
                ) : (
                  <Typography sx={{ color: "primary.main" }}>—</Typography>
                )}
              </Box>
            </Tooltip>
          )}
        </Box>
        <Box>
          {isNewSkill ? null : (
            <Stack
              direction='row'
              sx={{ justifyContent: "space-between" }}>
              <Tooltip title='Количество вопросов (подтвержденные/неподтвержденные)'>
                <Box>
                  <Typography
                    component='span'
                    sx={{ color: !skill.confirmedQuestionsCount ? "error.contrastText" : "primary.main" }}>
                    {skill.confirmedQuestionsCount || 0}
                  </Typography>
                  <Typography
                    component='span'
                    sx={{ color: "primary.main" }}>
                    /{skill.notConfirmedQuestionsCount || 0}
                  </Typography>
                </Box>
              </Tooltip>
              {!isNewSkill && knowledgeBlock.id && (
                <Tooltip title='Сгенерировать вопросы'>
                  <Button
                    size='small'
                    onClick={handleOpenGenerateModal}>
                    Сгенерировать
                  </Button>
                </Tooltip>
              )}
            </Stack>
          )}
        </Box>
        <Box sx={{ justifySelf: "end" }}>
          <Tooltip title='Удалить'>
            <IconButton
              onClick={handleRemoveSkill}
              disabled={!isEditable}>
              <Delete />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
    </div>
  );
};

export default withSelectionCheckbox(Skill);
